import React, { useState, useEffect } from "react";
import config from '../config/config';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";

const InvoicePaymentDialog = ({
  open,
  onClose,
  onSubmit,
  invoice,
  invoiceItems = [],
}) => {
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [items, setItems] = useState([]);

  // Fetch items from Laravel API
  useEffect(() => {
    const fetchItems = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(`${config.API_URL}/api/pullproductitems/Expense`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        // Sort items alphabetically by name
        const sortedItems = (res.data.invoiceItems || []).sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setItems(sortedItems);
      } catch (err) {
        console.error('Error fetching expense items:', err);
        setMessage('Failed to load items from the server.');
      }
    };

    if (open) {
      fetchItems();
    }
  }, [open]);

  // Initialize rows when dialog opens or invoiceItems/invoice changes
  useEffect(() => {
    if (open) {
      const initialRows = invoiceItems.map((item) => {
        const matchedItem = items.find((apiItem) => apiItem.name === item.description) || {};
        return {
          id: item.id,
          description: item.description || "",
          quantity: item.quantity || "1",
          total: item.total || matchedItem.price || "0.00",
          amountPaid: (
            parseFloat(item.total || matchedItem.price || 0) - parseFloat(item.paid || 0)
          ).toFixed(2),
          isNew: false,
        };
      });

      const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      // Only set rows if they haven't been modified (to preserve new rows)
      setRows((prevRows) => {
        if (prevRows.length === 0 || prevRows.every((row) => !row.isNew)) {
          return initialRows;
        }
        return prevRows;
      });

      setInvoiceDate(formatDate(invoice.invoiceIssueDate));
      setDueDate(formatDate(invoice.invoiceDueDate));
      setPaymentNote(invoice.note || "");
      setErrors({});
      setMessage(null);
      setLoading(false);
    }
  }, [open, invoiceItems, invoice, items]);

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;

    // If description changes, auto-populate total based on selected item's price
    if (field === "description") {
      const selectedItem = items.find((item) => item.name === value);
      if (selectedItem) {
        updatedRows[index].total = (
          parseFloat(selectedItem.price || 0) * parseFloat(updatedRows[index].quantity || 1)
        ).toFixed(2);
      } else {
        updatedRows[index].total = "0.00";
      }
    }

    // Recalculate total if quantity changes
    if (field === "quantity") {
      const selectedItem = items.find((item) => item.name === updatedRows[index].description);
      if (selectedItem) {
        updatedRows[index].total = (
          parseFloat(selectedItem.price || 0) * parseFloat(value || 1)
        ).toFixed(2);
      }
    }

    setRows(updatedRows);
  };

  const handleAddRow = () => {
    setRows((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        description: "",
        quantity: "1",
        total: "0.00",
        amountPaid: "0.00",
        isNew: true,
      },
    ]);
  };

  const handleDeleteRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  const handleEditSave = async () => {
    setLoading(true);
    setErrors({});
    setMessage(null);

    // Validate rows
    const newErrors = {};
    rows.forEach((row, index) => {
      if (!row.description) {
        newErrors[`description-${index}`] = "Description is required";
      }
      if (!row.quantity || row.quantity <= 0) {
        newErrors[`quantity-${index}`] = "Quantity must be greater than 0";
      }
      if (!row.total || row.total < 0) {
        newErrors[`total-${index}`] = "Total must be non-negative";
      }
      if (!row.amountPaid || row.amountPaid < 0) {
        newErrors[`amountPaid-${index}`] = "Amount Paid must be non-negative";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      const payload = {
        invoice_id: invoice.id,
        invoice_date: invoiceDate,
        due_date: dueDate,
        payment_note: paymentNote,
        payments: rows.map((row) => ({
          ...(row.isNew ? {} : { item_id: row.id }),
          description: row.description,
          quantity: parseFloat(row.quantity),
          total: parseFloat(row.total),
          amount_paid: parseFloat(row.amountPaid),
        })),
      };

      const response = await onSubmit(payload);

      if (response?.errors) {
        setErrors(response.errors);
      } else if (response?.message) {
        setMessage(response.message);
      }

      if (!response?.errors) {
        onClose();
      }
    } catch (error) {
      setMessage("An error occurred during saving.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Expense</DialogTitle>
      <DialogContent>
        {message && (
          <Typography color="error" gutterBottom>
            {message}
          </Typography>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <TextField
              label="Invoice Date"
              type="date"
              fullWidth
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              error={!!errors.invoiceDate}
              helperText={errors.invoiceDate}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Due Date"
              type="date"
              fullWidth
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              error={!!errors.dueDate}
              helperText={errors.dueDate}
            />
          </Grid>

          {rows.map((row, index) => (
            <React.Fragment key={row.id}>
              <Grid item xs={3}>
                <FormControl fullWidth error={!!errors[`description-${index}`]}>
                  <InputLabel id={`description-label-${index}`}>Description</InputLabel>
                  <Select
                    labelId={`description-label-${index}`}
                    value={items.find((item) => item.name === row.description) ? row.description : ""}
                    onChange={(e) =>
                      handleInputChange(index, "description", e.target.value)
                    }
                    disabled={loading || !row.isNew}
                    label="Description"
                  >
                    <MenuItem value="">Select an item</MenuItem>
                    {items.map((item) => (
                      <MenuItem key={item.id} value={item.name}>
                        {item.name} - {parseFloat(item.price).toFixed(2)}
                      </MenuItem>
                    ))}
                    {!row.isNew && row.description && !items.find((item) => item.name === row.description) && (
                      <MenuItem value={row.description} disabled>
                        {row.description} (Not in current list)
                      </MenuItem>
                    )}
                  </Select>
                  {!!errors[`description-${index}`] && (
                    <Typography color="error" variant="caption">
                      {errors[`description-${index}`]}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={2}>
                <TextField
                  label="Qty"
                  type="number"
                  fullWidth
                  value={row.quantity}
                  onChange={(e) =>
                    handleInputChange(index, "quantity", e.target.value)
                  }
                  disabled={loading}
                  error={!!errors[`quantity-${index}`]}
                  helperText={errors[`quantity-${index}`]}
                />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  label="Total"
                  type="number"
                  fullWidth
                  value={row.total}
                  onChange={(e) =>
                    handleInputChange(index, "total", e.target.value)
                  }
                  disabled={loading || row.isNew}
                  error={!!errors[`total-${index}`]}
                  helperText={errors[`total-${index}`]}
                />
              </Grid>
             {/* <Grid item xs={3}>
                <TextField
                  label="Amount Paid"
                  type="number"
                  fullWidth
                  value={row.amountPaid}
                  onChange={(e) =>
                    handleInputChange(index, "amountPaid", e.target.value)
                  }
                  disabled={loading}
                  error={!!errors[`amountPaid-${index}`]}
                  helperText={errors[`amountPaid-${index}`]}
                />
              </Grid>*/}
              <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
                {row.isNew && (
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteRow(index)}
                    disabled={loading}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Grid>
            </React.Fragment>
          ))}

         {/* <Grid item xs={12}>
            <Button variant="outlined" onClick={handleAddRow} disabled={loading}>
              + Add Item
            </Button>
          </Grid>
          */}

          <Grid item xs={12}>
            <TextField
              label="Payment Note"
              fullWidth
              multiline
              rows={3}
              value={paymentNote}
              onChange={(e) => setPaymentNote(e.target.value)}
              disabled={loading}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleEditSave} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePaymentDialog;